import React, { useEffect } from "react";
import { Typography, Row, Col, Rate, Statistic, Checkbox } from "antd";
import dayjs from "dayjs";
import { CDN, PADZERO } from "Constants";
import { ImageResizer } from "components/ImageResizer/ImageResizer";

const { Text, Title, Paragraph } = Typography;

/**
 * Presentational report body, extracted from Report.js (sdd/per-equipment-pdf-reports
 * Phase 3) so it can be reused for BOTH:
 *   - the consolidated, interactive Informe-tab report (Report.js renders this
 *     with `interactive` + `showStats` + live state callbacks), and
 *   - each standalone per-equipment PDF captured by perEquipmentZip.js
 *     (rendered offscreen with `interactive={false}` and a single-vm
 *     `maintenances` array).
 *
 * This is a pure presentational component: it owns NO state. Every piece of
 * data it needs (order fields, view models, the current photo selection, and
 * the callbacks to mutate that selection) is injected via props, so the
 * SAME rendering logic produces identical output for both consumers — the
 * project's non-regression requirement for this extraction.
 *
 * `resolveSelectedPhotos` here is a caller-bound closure of the shared
 * `reportPhotos.js` helper: `(mIdx, tIdx, aIdx, files) => string[]`. Callers
 * close over their own `photoSelection`/`maxPhotos` before passing it down.
 */
const ReportDocument = React.forwardRef((props, ref) => {
  const {
    order = {},
    maintenances = [],
    resumenEjecutivoAI,
    deliver = {},
    created_by,
    maxPhotos,
    interactive = true,
    showStats = true,
    resolveSelectedPhotos,
    compressedImages = {},
    onTogglePhoto,
    onEditResumen,
    onEditRecomendacion,
  } = props;

  const { type = null, code = "", customer, subsidiary } = order;
  const { questions = [] } = deliver;

  console.log(compressedImages)

  useEffect(()=>{

  },[])

  const renderDate = () => {
    const first = maintenances[0];
    if (first?.start) return <Text>{dayjs(first.start).format("YYYY/MM/DD")}</Text>;
    return <Text>...</Text>;
  };

  const equipos_funcionando = maintenances.filter(
    (m) => m?.equipmentStateCode == 1
  ).length;
  const equipos_novedad = maintenances.filter(
    (m) => m?.equipmentStateCode == 2
  ).length;
  const equipos_no_funcionando = maintenances.filter(
    (m) => m?.equipmentStateCode == 3
  ).length;

  return (
    <div ref={ref} className="preview-pdf">
      <div>
        <table
          width="100%"
          style={{ marginBottom: 16, position: "relative", zIndex: 99 }}
        >
          <tr>
            <td width="30%" valign="top">
              <img
                width="80%"
                src={`${CDN}/website/logo_color.png?t=${Date.now()}`}
              />
            </td>
            <td>
              <Title level={2}>Reporte de Servicio</Title>
            </td>
          </tr>
        </table>
        <br />
        <table width="100%" className="cabecera-reporte-orden">
          <tr>
            <th>Servicio</th>
            <td>
              <Text>
                {type && type.name}{" "}
                {type && `${type.cod}-${code.toString().padStart(PADZERO, 0)}`}
              </Text>
            </td>
            <th>Fecha</th>
            <td>{renderDate()}</td>
          </tr>
          <tr>
            <th>Cliente</th>
            <td>
              <Text>{customer && customer.name}</Text>
            </td>
            <th>Sucursal</th>
            <td>
              <Text>{subsidiary && subsidiary.name}</Text>
            </td>
          </tr>
          <tr>
            <th>Dirección</th>
            <td>
              <Text>{subsidiary && subsidiary.address}</Text>
            </td>
            <th>Contacto</th>
            <td>{subsidiary && subsidiary.contact}</td>
          </tr>
        </table>

        {showStats && (
          <div className="reporte-estadisticas">
            <Row gutter={16}>
              <Col span={8}>
                <Statistic
                  title="Equipos funcionando"
                  value={equipos_funcionando}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Equipos con novedad"
                  value={equipos_novedad}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Equipos fuera de servicio"
                  value={equipos_no_funcionando}
                />
              </Col>
            </Row>
          </div>
        )}

        {resumenEjecutivoAI?.reply && (
          <div className="resumen-ejecutivo-ia">
            <span className="resumen-label">
              ✨ Resumen ejecutivo
            </span>
            <Paragraph
              editable={interactive && onEditResumen ? { onChange: onEditResumen } : false}
            >
              {resumenEjecutivoAI.reply}
            </Paragraph>
          </div>
        )}

        <>
          {maintenances.map((m, mIdx) => (
            <section
              key={mIdx}
              className={mIdx > 0 ? "print-section" : ""}
              style={{ position: "relative", zIndex: 9 }}
            >
              <table className="cabecera-reporte-orden print-equipment-header">
                <tr>
                  <td
                    colSpan={2}
                    style={{
                      background: m.equipmentStateColor,
                      textAlign: "center",
                    }}
                  >
                    <Text
                      style={{ color: "#ffff", fontWeight: "bold" }}
                      level={5}
                    >
                      {m.equipment}
                    </Text>
                    <br />
                    <Text style={{ color: "#ffff" }}>{m.equipmentState}</Text>
                  </td>
                </tr>
                <tr>
                  <th>Zona suministros</th>
                  <td>{m.supply_zone}</td>
                </tr>
                <tr>
                  <th>Observaciones finales</th>
                  <td>
                    {m.observations}
                  </td>
                </tr>
                {m.recomendacionesAI?.reply && (
                  <tr>
                    <th>Recomendaciones</th>
                    <td>
                      <div className="recomendaciones-ia">
                        <span className="recomendaciones-label">
                          Recomendaciones
                        </span>
                        <Paragraph
                          editable={
                            interactive && onEditRecomendacion
                              ? {
                                  onChange: (t) =>
                                    onEditRecomendacion(m.maintenanceId, m.recomendacionesAI, t),
                                }
                              : false
                          }
                        >
                          {m.recomendacionesAI.reply}
                        </Paragraph>
                      </div>
                    </td>
                  </tr>
                )}
              </table>

              <Title level={3}>Actividades realizadas</Title>
              {m.tasks
                .filter((t) =>
                  t.activitys.some(
                    (a) => a.visible === true || a.visible == undefined
                  )
                )
                .map((t, tIdx) => (
                  <React.Fragment key={tIdx}>
                    {t.activitys
                      .filter((a) => a.visible)
                      .map((a, aIdx) => {
                        const selectedFiles = resolveSelectedPhotos(
                          mIdx,
                          tIdx,
                          aIdx,
                          a.file || []
                        );
                        const selectedSet = new Set(selectedFiles);
                        const selectedCount = selectedSet.size;
                        
                        return (
                          <div key={aIdx} className="print-activity">
                            <Title level={5}>{a.name}</Title>
                            <Text style={{ textAlign: "left" }}>
                              {a.reply}
                            </Text>
                            {a.file && a.file.length > 0 && (
                              <div className="photo-selector-controls">
                                <Text
                                  type="secondary"
                                  style={{
                                    fontSize: 12,
                                    marginBottom: 4,
                                    display: "block",
                                  }}
                                >
                                  Fotos en PDF: {selectedCount}/{maxPhotos}
                                </Text>
                              </div>
                            )}
                            <div
                              className="print-photos-grid"
                              style={{
                                display: "grid",
                                gap: "1%",
                                gridTemplateColumns: "repeat(3, 1fr)",
                                gridAutoRows: "minmax(50px, auto)",
                                margin: "0 auto",
                                padding: 16,
                              }}
                            >
                              
                              {
                                
                              a.file &&
                                a.file.map((f, fi) => {
                                  const isSelected = selectedSet.has(f);
                                  
                                  return (
                                    <figure
                                      key={fi}
                                      className={
                                        isSelected
                                          ? "print-figure"
                                          : "print-figure photo-unselected"
                                      }
                                      style={{
                                        alignItems: "center",
                                        justifyContent: "center",
                                        position: "relative",
                                        opacity: isSelected ? 1 : 0.35,
                                      }}
                                    >
                                      {/*<img
                                        id={`evidencia-${f.split("/").pop()}`}
                                        className="report-photo"
                                        src={compressedImages[f] || f}
                                        data-original-src={f}
                                        style={{
                                          maxWidth: "100%",
                                          borderRadius: 4,
                                        }}
                                      />*/}
                                      <ImageResizer imageUrl={f} maxWidth={200}/>
                                      {interactive && (
                                        <div className="photo-selector-controls">
                                          <Checkbox
                                            checked={isSelected}
                                            onChange={() =>
                                              onTogglePhoto &&
                                              onTogglePhoto(
                                                mIdx,
                                                tIdx,
                                                aIdx,
                                                f,
                                                a.file
                                              )
                                            }
                                            style={{
                                              position: "absolute",
                                              top: 4,
                                              left: 4,
                                            }}
                                          />
                                        </div>
                                      )}
                                    </figure>
                                  );
                                })}
                            </div>
                          </div>
                        );
                      })}
                  </React.Fragment>
                ))}
            </section>
          ))}

          {deliver && deliver.show && (
            <div
              className="pdf-encuesta"
              style={{
                breakInside: "avoid",
                marginTop: 64,
                position: "relative",
                zIndex: 99,
                marginLeft: "35%",
              }}
            >
              {questions.map((q, i) => (
                <div key={i}>
                  <Title level={5}>
                    <p>{q.q}</p>
                    {q.v == 1 ? (
                      <Text type="success">Si</Text>
                    ) : (
                      <Text type="warning">No</Text>
                    )}
                  </Title>
                </div>
              ))}
              <Title level={5}>Comó calificarias el servicio?</Title>
              <Rate disabled defaultValue={deliver.score} />
              <img
                src={deliver.signature}
                style={{ width: "30%", margin: "0 auto", display: "block" }}
              />
            </div>
          )}
  <br></br>
  <br></br>
          <table className="reporte-firma" width={"100%"}>
            <tr>
             
              <td width={"50%"} style={{ borderTop: "1px solid black" }}>
                
                {created_by?.signature && (
                  <img
                    src={created_by.signature}
                    alt="Firma"
                    style={{ maxHeight: 50, display: "block", margin: "0 auto" }}
                  />
                )}
                {created_by?.name}
              </td>
              <td></td>
            </tr>
            <tr>
              <td>Ing. Responsable</td>
              <td></td>
            </tr>
          </table>
        </>
      </div>
    </div>
  );
});

export default ReportDocument;
